import { __ } from "@wordpress/i18n";
import { useSettings } from "../../context/SettingsContext";
import { Toggle, SettingRow, SectionCard, ColorInput, NumberInput, TextInput, InfoBox, ProBadge } from "../ui/index";
import { isPremium } from "../../utils/isPremium";

export default function GeneralPanel() {
    const {
        state: { settings },
        actions,
    } = useSettings();
    const s = (key, val) => actions.set(key, val);

    return (
        <div className="space-y-5">
            <SectionCard
                title={__("Variation Images", "wpbean-product-gallery-slider-for-woocommerce")}
                description={__("Control how product variation images interact with the gallery.", "wpbean-product-gallery-slider-for-woocommerce")}
            >
                <SettingRow
                    label={__("Variation Image Change", "wpbean-product-gallery-slider-for-woocommerce")}
                    description={__("Automatically swap the main image and add a gallery entry when a product variation with its own image is selected.", "wpbean-product-gallery-slider-for-woocommerce")}
                >
                    <Toggle
                        value={settings.variationImageChange}
                        onChange={(v) => s("variationImageChange", v)}
                    />
                </SettingRow>

                <SettingRow
                    label={
                        <span className="inline-flex items-center gap-1.5">
                            {__("Variation Gallery", "wpbean-product-gallery-slider-for-woocommerce")}
                            {!isPremium() && <ProBadge />}
                        </span>
                    }
                    description={__("Allow each variation to have its own set of gallery thumbnail images. When a variation is selected the entire gallery is replaced with the variation's images. Requires the gallery field to be filled in on each product variation.", "wpbean-product-gallery-slider-for-woocommerce")}
                >
                    <Toggle
                        value={settings.variationGallery}
                        onChange={(v) => s("variationGallery", v)}
                        disabled={!isPremium()}
                    />
                </SettingRow>
            </SectionCard>

            <SectionCard
                title={__("Performance", "wpbean-product-gallery-slider-for-woocommerce")}
                description={__("Control image loading behavior.", "wpbean-product-gallery-slider-for-woocommerce")}
            >
                <SettingRow
                    label={__("Preload Images", "wpbean-product-gallery-slider-for-woocommerce")}
                    description={__("Preload all gallery and zoom images in the background on page load for a smoother experience.", "wpbean-product-gallery-slider-for-woocommerce")}
                >
                    <Toggle
                        value={settings.preloadImages}
                        onChange={(v) => s("preloadImages", v)}
                    />
                </SettingRow>

                <SettingRow
                    label={__("Zoom Before Image Loads", "wpbean-product-gallery-slider-for-woocommerce")}
                    description={__("Allow zoom to activate immediately without waiting for the image to finish loading. Off by default — disabling the wait ensures zoom window sizing is always accurate.", "wpbean-product-gallery-slider-for-woocommerce")}
                >
                    <Toggle
                        value={settings.zoomBeforeLoad}
                        onChange={(v) => s("zoomBeforeLoad", v)}
                    />
                </SettingRow>
            </SectionCard>

            <SectionCard
                title={__("Loading Spinner", "wpbean-product-gallery-slider-for-woocommerce")}
                description={__("Show an animated spinner while images are loading.", "wpbean-product-gallery-slider-for-woocommerce")}
            >
                <SettingRow
                    label={__("Enable Spinner", "wpbean-product-gallery-slider-for-woocommerce")}
                    description={__("Display a loading indicator while images are being fetched.", "wpbean-product-gallery-slider-for-woocommerce")}
                >
                    <Toggle
                        value={settings.spinner}
                        onChange={(v) => s("spinner", v)}
                    />
                </SettingRow>

                {settings.spinner && (
                    <SettingRow
                        label={__("Spinner Color", "wpbean-product-gallery-slider-for-woocommerce")}
                        description={__("The color of the loading spinner animation.", "wpbean-product-gallery-slider-for-woocommerce")}
                    >
                        <ColorInput
                            value={settings.spinnerColor}
                            onChange={(v) => s("spinnerColor", v)}
                        />
                    </SettingRow>
                )}
            </SectionCard>
            <SectionCard
                title={
                    <span className="inline-flex items-center gap-1.5">
                        {__("Sticky Summary", "wpbean-product-gallery-slider-for-woocommerce")}
                        {!isPremium() && <ProBadge />}
                    </span>
                }
                description={__("Keep the product summary (title, price, add-to-cart) fixed in the viewport while scrolling. Designed for wide-screen layouts where the gallery and summary sit side by side.", "wpbean-product-gallery-slider-for-woocommerce")}
            >
                <SettingRow
                    label={__("Enable", "wpbean-product-gallery-slider-for-woocommerce")}
                    description={__("Apply sticky positioning to the product summary alongside the gallery.", "wpbean-product-gallery-slider-for-woocommerce")}
                >
                    <Toggle
                        value={
                            settings.stackedStickyEnabled !== false &&
                            !!settings.stackedStickyEnabled
                        }
                        onChange={(v) => s("stackedStickyEnabled", v)}
                        disabled={!isPremium()}
                    />
                </SettingRow>

                {settings.stackedStickyEnabled && (
                    <>
                        <SettingRow
                            label="Fix Theme Overflow"
                            description="Automatically override overflow:hidden on parent elements that would block the sticky effect. Disable if your theme layout breaks with this on."
                        >
                            <Toggle
                                value={settings.stackedStickyFixOverflow !== false}
                                onChange={(v) => s("stackedStickyFixOverflow", v)}
                            />
                        </SettingRow>

                        <SettingRow
                            label="Summary Selector"
                            description="CSS selector for the product summary element. Default targets the standard WooCommerce summary div."
                        >
                            <TextInput
                                value={settings.stackedStickySelector}
                                onChange={(v) => s("stackedStickySelector", v)}
                                placeholder=".summary.entry-summary"
                                width="w-52"
                            />
                        </SettingRow>

                        <SettingRow
                            label="Top Offset"
                            description="Distance from the top of the viewport when the summary is in sticky position."
                        >
                            <NumberInput
                                value={settings.stackedStickyTop}
                                onChange={(v) => s("stackedStickyTop", v)}
                                min={0}
                                max={300}
                                unit="px"
                                width="w-24"
                            />
                        </SettingRow>

                        <SettingRow
                            label="Disable Below"
                            description="Viewport width below which sticky is disabled — keeps the effect large-screen only. Set to 0 to always enable."
                        >
                            <NumberInput
                                value={settings.stackedStickyBreakpoint}
                                onChange={(v) => s("stackedStickyBreakpoint", v)}
                                min={0}
                                max={2560}
                                unit="px"
                                width="w-24"
                            />
                        </SettingRow>

                        <InfoBox className="mt-2 mb-2">
                            Sticky summary works when the gallery and summary are
                            in a <strong>side-by-side flex or grid layout</strong> —
                            the most common WooCommerce single-product template.
                            If your theme stacks them vertically, this option has
                            no visible effect.
                        </InfoBox>
                    </>
                )}
            </SectionCard>
        </div>
    );
}
